import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { mediaUrl } from "@/lib/api";
import { Loader2, X, TrendingUp, TrendingDown } from "lucide-react";

const ENTERPRISE = { color: "#35A787", hover: "#2D8F74" };
export const ACCENTS = {
  ops: { ...ENTERPRISE, name: "Operations", sub: "Admin & operations" },
  business: { ...ENTERPRISE, name: "Advertiser", sub: "Grow your business" },
  partner: { ...ENTERPRISE, name: "Driver", sub: "Drive & earn" },
};

export function Logo({ size = 32, accent, className, variant = "color" }) {
  const src = variant === "white" ? "/anand-logo-white.png" : "/anand-logo.png";
  return (
    <img
      src={src}
      alt="Anand"
      style={{ height: size, width: "auto" }}
      className={className}
    />
  );
}

// ---- Status badge (minimal, soft-tint pill with indicator dot) ----
const STATUS_MAP = {
  available: { label: "Available", dot: "bg-emerald-500", pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  booked: { label: "Booked", dot: "bg-amber-500", pill: "bg-amber-50 text-amber-700 border-amber-200" },
  offline: { label: "Offline", dot: "bg-slate-400", pill: "bg-slate-50 text-slate-600 border-slate-200" },
  faulty: { label: "Faulty", dot: "bg-rose-500", pill: "bg-rose-50 text-rose-700 border-rose-200" },
  approved: { label: "Approved", dot: "bg-emerald-500", pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  in_review: { label: "In review", dot: "bg-amber-500", pill: "bg-amber-50 text-amber-700 border-amber-200" },
  rejected: { label: "Rejected", dot: "bg-rose-500", pill: "bg-rose-50 text-rose-700 border-rose-200" },
  verified: { label: "Verified", dot: "bg-emerald-500", pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  submitted: { label: "Pending review", dot: "bg-amber-500", pill: "bg-amber-50 text-amber-700 border-amber-200" },
  overdue: { label: "Overdue", dot: "bg-rose-500", pill: "bg-rose-50 text-rose-700 border-rose-200" },
  not_due: { label: "Up to date", dot: "bg-slate-400", pill: "bg-slate-50 text-slate-600 border-slate-200" },
  pending: { label: "Pending", dot: "bg-amber-500", pill: "bg-amber-50 text-amber-700 border-amber-200" },
  paid: { label: "Paid", dot: "bg-emerald-500", pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  open: { label: "Open", dot: "bg-rose-500", pill: "bg-rose-50 text-rose-700 border-rose-200" },
  closed: { label: "Closed", dot: "bg-slate-400", pill: "bg-slate-50 text-slate-600 border-slate-200" },
};
const FALLBACK = { label: (s) => s, dot: "bg-slate-400", pill: "bg-slate-50 text-slate-600 border-slate-200" };

export function StatusBadge({ status }) {
  const s = STATUS_MAP[status];
  const { label, dot, pill } = s ? s : { ...FALLBACK, label: status };
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap", pill)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", dot)} />
      {label}
    </span>
  );
}

// ---- Enterprise KPI card ----
export function KPI({ label, value, sub, accent, testid, icon: Icon, delta, dark }) {
  const ac = accent || "#35A787";
  const trend = typeof delta === "number" ? (delta >= 0 ? { dir: "up", text: delta > 0 ? `${delta}%` : "steady" } : { dir: "down", text: `${Math.abs(delta)}%` }) : null;
  const positive = !trend || trend.dir === "up";
  return (
    <div
      data-testid={testid}
      className={cn("ent-kpi p-6", dark && "!bg-slate-900 !border-slate-800")}
    >
      <div className="flex items-start justify-between gap-3">
        <span className={cn("text-[11px] font-semibold uppercase tracking-[0.08em]", dark ? "text-slate-400" : "text-[#6B6659]")}>{label}</span>
        {Icon && (
          <span className="kpi-icon h-10 w-10 flex-none" style={{ background: ac + "1a", color: ac }}>
            <Icon size={18} strokeWidth={2.4} />
          </span>
        )}
      </div>
      <div className={cn("mt-3 font-mono text-[32px] font-bold leading-none tracking-tight tabular", dark ? "text-white" : "text-[#14130E]")}>
        {value}
      </div>
      {sub && <div className={cn("mt-2 text-[13px]", dark ? "text-slate-500" : "text-[#9CA3AF]")}>{sub}</div>}
      {trend && (
        <div className={cn("mt-3 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-semibold",
          positive ? "bg-emerald-50 text-[#059669]" : "bg-red-50 text-[#C8102E]")}>
          {trend.dir === "up" ? <TrendingUp size={12} strokeWidth={2.4} /> : <TrendingDown size={12} strokeWidth={2.4} />}
          {trend.text}
        </div>
      )}
    </div>
  );
}

// ---- On-air rotation widget ----
export function AdRotator({ ads, light }) {
  const list = ads && ads.length ? ads : [];
  const [i, setI] = useState(0);
  const [p, setP] = useState(0);
  const vref = useRef(null);
  const dur = list[i]?.duration || 15;
  const hasVideo = !!list[i]?.video;
  const next = () => setI((n) => (n + 1) % Math.max(1, list.length));
  useEffect(() => {
    setP(0);
    if (hasVideo && vref.current) {
      const t = setInterval(() => {
        const v = vref.current;
        if (v && v.duration) setP((v.currentTime / v.duration) * 100);
      }, 100);
      return () => clearInterval(t);
    }
    const step = 100 / (dur * 10);
    const t = setInterval(() => {
      setP((x) => {
        if (x + step >= 100) { next(); return 0; }
        return x + step;
      });
    }, 100);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [i, dur, list.length, hasVideo]);
  if (!list.length) return <div className="text-sm text-stone-400">No ads on air.</div>;
  const cur = list[i];
  return (
    <div className="grid gap-5 sm:grid-cols-[1.5fr_1fr]">
      <div className="relative aspect-video overflow-hidden rounded-lg bg-slate-950">
        {hasVideo ? (
          <video key={cur.id} ref={vref} src={mediaUrl(cur.video)} autoPlay muted playsInline
            className="absolute inset-0 h-full w-full object-cover" onEnded={next} />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
            <div className="font-display text-xl font-semibold text-white">{cur.title}</div>
            <div className="mt-1 text-xs font-medium text-stone-400">{cur.advertiser_name}</div>
          </div>
        )}
        <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-md bg-slate-900/80 px-2 py-1 text-[10px] font-semibold text-stone-300">
          <span className="live-dot h-1.5 w-1.5 rounded-full bg-rose-500" /> On air · {i + 1}/{list.length}
        </div>
        <div className="absolute bottom-0 left-0 h-0.5 bg-white" style={{ width: p + "%", transition: "width .1s linear" }} />
      </div>
      <div className="space-y-1.5">
        <div className="text-[11px] font-medium uppercase tracking-wider text-stone-400">Rotation · {list.length} brand{list.length === 1 ? "" : "s"} / minute</div>
        {list.map((a, n) => (
          <div key={a.id} className={cn("flex items-center gap-2 rounded-md border px-2.5 py-2 text-sm",
            n === i ? "border-stone-300 bg-stone-50" : "border-stone-200 bg-white")}>
            <span className="text-xs text-stone-300">{n + 1}</span>
            <span className="min-w-0 flex-1 truncate font-medium text-stone-800">{a.advertiser_name}</span>
            <span className="text-xs tabular-nums text-stone-400">{Math.round(a.duration)}s</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function slotsSummary(cabs) {
  let total = 0, used = 0;
  for (const c of cabs || []) {
    total += c.slots_total ?? 4;
    used += c.slots_used ?? (c.status === "booked" && c.advertiser_id ? 1 : 0);
  }
  return { total, used, free: total - used };
}

// ---- Dense slot grid ----
const CELL = { available: "#10B981", booked: "#D97706", offline: "#6B7280", faulty: "#EF4444" };
export function SlotGrid({ cabs, onCellClick, selectedIds = [], testid }) {
  const color = (c) => (selectedIds.includes(c.id) ? "#2563EB" : CELL[c.status] || "#6B7280");
  const testFor = (s) => ({ available: "admin-slot-item-green", booked: "admin-slot-item-amber", offline: "admin-slot-item-grey", faulty: "admin-slot-item-red" }[s]);
  const short = (n) => (n || "").split(/\s+/).map((w) => w[0]).join("").slice(0, 3).toUpperCase();
  const sub = (c) => {
    if (c.status === "booked") return short(c.advertiser_name);
    if (c.status === "available") return `${c.slots_free ?? 4} free`;
    if (c.status === "offline") return "off";
    if (c.status === "faulty") return "fault";
    return "";
  };
  return (
    <div data-testid={testid} className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(38px, 1fr))" }}>
      {cabs.map((c) => {
        const ctx = color(c);
        const total = c.slots_total ?? 4;
        const used = c.slots_used ?? (c.status === "booked" && c.advertiser_id ? 1 : 0);
        const free = c.slots_free ?? total - used;
        const mix = (c.slots || []).map((s, i) => `S${i + 1} ${s.advertiser_name || "empty"}`).join(" · ");
        return (
          <button
            key={c.id}
            data-testid={testFor(c.status)}
            title={`${c.plate} · ${c.status}${c.advertiser_name ? " · " + c.advertiser_name : ""} · ${used} of ${total} slots used · ${free} free${mix ? "\n" + mix : ""}`}
            onClick={() => onCellClick && onCellClick(c)}
            className="slot tap aspect-square flex flex-col items-center justify-center gap-1 rounded-md border bg-white"
            style={{ borderColor: ctx + "55", background: ctx + "0d" }}
          >
            <span className="text-[9px] font-mono font-semibold leading-none" style={{ color: ctx }}>{c.plate.slice(-4)}</span>
            <span className="text-[8px] font-bold leading-none" style={{ color: ctx }}>{sub(c)}</span>
            <span className="flex gap-[2px]">
              {Array.from({ length: total }).map((_, n) => (
                <span key={n} className="h-[4px] w-[4px] rounded-full" style={{ background: n < used ? ctx : ctx + "33" }} />
              ))}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function Legend() {
  const items = [["Available", "#10B981"], ["Booked", "#D97706"], ["Offline", "#6B7280"], ["Faulty", "#EF4444"]];
  return (
    <div className="flex flex-wrap gap-4 text-xs text-stone-500">
      {items.map(([l, c]) => (
        <span key={l} className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm" style={{ background: c }} />{l}
        </span>
      ))}
      <span className="flex items-center gap-1.5"><span className="h-[6px] w-[6px] rounded-full bg-stone-400" /><span className="h-[6px] w-[6px] rounded-full bg-stone-400/30" />dot = slot · solid = taken</span>
    </div>
  );
}

// ---- Auth screen ----
export function LoginScreen({ portal, demoCreds, isPhone }) {
  const { login, config } = useAuth();
  const acc = ACCENTS[portal];
  const brand = config?.brand?.name || "Anand";
  const demoMode = config?.brand?.demo_mode !== false;
  const [identifier, setId] = useState("");
  const [password, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setErr(""); setLoading(true);
    const r = await login(identifier, password, portal);
    setLoading(false);
    if (!r.ok) setErr(r.error);
  };

  const fill = async (c) => {
    setId(c.id); setPw(c.pw);
    setErr(""); setLoading(true);
    const r = await login(c.id, c.pw, portal);
    setLoading(false);
    if (!r.ok) setErr(r.error);
  };

  return (
    <div className="relative flex min-h-screen items-stretch overflow-hidden bg-[#ffffff]">
      {/* Left enterprise brand panel */}
      <div className="relative hidden w-[42%] max-w-[520px] flex-col justify-between overflow-hidden bg-white p-12 lg:flex">
        <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(180deg, #F7FCFA, #FFFFFF)" }} />
        <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full" style={{ background: "radial-gradient(circle, rgba(53,167,135,.18), transparent 60%)" }} />
        <div className="pointer-events-none absolute -bottom-32 -right-20 h-80 w-80 rounded-full" style={{ background: "radial-gradient(circle, rgba(255,196,0,.14), transparent 60%)" }} />
        <div className="relative flex items-center gap-3">
          <Logo size={34} className="max-w-[170px] object-contain" />
          <span className="font-display text-lg font-bold tracking-tight text-[#14130E]">{brand}</span>
        </div>
        <div className="relative">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ borderColor: "#35A78755", color: "#2D8F74" }}>
            <span className="h-1.5 w-1.5 rounded-full bg-[#35A787]" /> In-cab digital advertising
          </div>
          <h1 className="font-display text-[34px] font-bold leading-[1.08] tracking-tight text-[#14130E]">Reach thousands of<br />riders across Pune.</h1>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-[#6B6659]">Book ads on real cabs, track plays live, and grow your brand — from one enterprise dashboard.</p>
        </div>
        <div className="relative grid grid-cols-3 gap-6 border-t border-[#EAE9E4] pt-6">
          {[["68", "cabs live"], ["720", "plays/day"], ["132km", "daily reach"]].map(([v, l]) => (
            <div key={l}>
              <div className="font-mono text-xl font-semibold text-[#14130E]">{v}</div>
              <div className="text-[11px] uppercase tracking-wider text-[#8A887F]">{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="relative flex flex-1 items-center justify-center px-4 py-10">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0" style={{ background: `radial-gradient(700px 420px at 85% -10%, ${acc.color}14, transparent 55%)` }} />
        </div>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
          className="relative w-full max-w-md">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <Logo size={28} className="max-w-[130px] object-contain" />
            <span className="font-display text-lg font-bold tracking-tight text-[#1F2937]">{brand}</span>
          </div>
          <div className="mb-6">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#B8860B]">{acc.name} portal</div>
            <h2 className="mt-1 font-display text-2xl font-bold tracking-tight text-[#1F2937]">Welcome back</h2>
            <p className="mt-1 text-sm text-[#6B6659]">Sign in to your account to continue. Wrong-portal accounts are rejected.</p>
          </div>
          <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-[0_1px_3px_rgba(16,24,40,0.06),0_24px_60px_-28px_rgba(16,24,40,0.3)]">
            <form onSubmit={submit} className="space-y-3.5" autoComplete="off">
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[#6B6659]">{isPhone ? "Phone number" : "Work email"}</label>
                <input data-testid="login-email-input" value={identifier} onChange={(e) => setId(e.target.value)} autoComplete="off"
                  className="tap w-full rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-2.5 text-sm text-[#1F2937] outline-none transition-all placeholder:text-[#9CA3AF] hover:border-[#D1D5DB] focus:border-transparent focus:ring-2 focus:ring-[#35A787]/40"
                  placeholder={isPhone ? "9812340001" : "you@company.com"} />
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-[#6B6659]">{isPhone ? "PIN" : "Password"}</label>
                <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPw(e.target.value)} autoComplete="new-password"
                  className="tap w-full rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-2.5 text-sm text-[#1F2937] outline-none transition-all placeholder:text-[#9CA3AF] hover:border-[#D1D5DB] focus:border-transparent focus:ring-2 focus:ring-[#35A787]/40"
                  placeholder="••••••••" />
              </div>
              {err && <div data-testid="login-error" className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-[#C8102E]">{err}</div>}
              <button data-testid="login-submit-button" type="button" onClick={submit} disabled={loading}
                className="ent-btn w-full bg-[#35A787] text-white shadow-[0_1px_3px_rgba(53,167,135,0.25),0_8px_20px_-8px_rgba(53,167,135,0.55)] hover:bg-[#2D8F74] hover:shadow-[0_10px_24px_-8px_rgba(53,167,135,0.6)] disabled:opacity-60">
                {loading && <Loader2 className="animate-spin" size={15} />} Sign in
              </button>
            </form>
            {demoMode && demoCreds && (
              <div className="mt-5 border-t border-[#F1F3F5] pt-4">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[#6B6659]">One-click demo</div>
                <div className="space-y-1.5">
                  {demoCreds.map((c, i) => (
                    <button key={i} data-testid="demo-autofill-button" onClick={() => fill(c)}
                      className="group flex w-full items-center justify-between rounded-lg border border-[#E5E7EB] px-3 py-2 text-left text-xs text-[#4B5563] transition-all hover:border-[#35A787]/60 hover:bg-[#F0FAF7]">
                      <span className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: acc.color }} />
                        <span className="font-mono">{c.id}</span>
                      </span>
                      <span className="text-[#9CA3AF]">{c.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export function Modal({ open, onClose, children, title, wide, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-[2px]" />
      <motion.div initial={{ opacity: 0, scale: 0.97, y: 6 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.18, ease: [0.2, 0.7, 0.2, 1] }}
        data-testid={testid}
        onClick={(e) => e.stopPropagation()}
        className={cn("relative max-h-[90vh] w-full overflow-y-auto rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-[0_24px_80px_-28px_rgba(16,24,40,0.45)]", wide ? "max-w-2xl" : "max-w-md")}>
        {title && (
          <div className="mb-5 flex items-center justify-between">
            <h3 className="font-display text-lg font-bold tracking-tight text-[#1F2937]">{title}</h3>
            <button onClick={onClose} className="tap flex h-7 w-7 items-center justify-center rounded-lg text-[#6B6659] transition-colors hover:bg-[#F3F4F6] hover:text-[#1F2937]"><X size={15} /></button>
          </div>
        )}
        {children}
      </motion.div>
    </div>
  );
}

// ---- Empty state ----
export function EmptyState({ icon: Icon, title, sub, action }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#D1D5DB] bg-white/70 px-6 py-16 text-center">
      {Icon && <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-[#6B6659] shadow-sm ring-1 ring-[#E5E7EB]"><Icon size={20} /></span>}
      <div className="mt-4 text-sm font-semibold text-[#1F2937]">{title}</div>
      {sub && <div className="mt-1 max-w-sm text-xs text-[#6B6659]">{sub}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}